// Miroir memoire synchrone des donnees user, analogue a contentStore pour la
// theorie. Le moteur et les utils non-React lisent ces valeurs de facon SYNCHRONE
// (ex: audioEngine.getInstrument, guitarNeckUtils au moment de l'usage). Le miroir
// est hydrate au boot depuis le store IndexedDB `userData`, puis tenu a jour en
// write-through : un set met a jour le miroir ET ecrit IndexedDB (dirty=true).
//
// Resout le piege async : une valeur lue depuis le miroir au MOMENT de l'usage
// voit l'hydratation ; une valeur capturee a l'import ne la verrait jamais. Les
// consommateurs doivent donc appeler getSetting()/getUserItems() a l'usage.
import { putUserData, applyPulled } from './userDataStore';

// scope -> { item_key -> data }. settings: data = valeur typee directe.
// collections (progression/preset/pattern/purchase): data = objet complet.
const mirror = { setting: {}, progression: {}, preset: {}, pattern: {}, purchase: {} };

let db = null; // handle IndexedDB pour le write-through (attache par DataProvider)

// File des écritures faites avant l'attachement de la base. Au boot, la
// réconciliation d'un achat peut tomber pendant que DataProvider ouvre encore
// IndexedDB : sans cette file, la persistance serait jetée en silence.
//
// Une entrée y reste jusqu'à ce que son écriture IndexedDB soit confirmée (ou
// perdue), pas seulement jusqu'à la vidange : hydrateUserMirror peut passer
// entre les deux et doit pouvoir la rejouer par-dessus les rows relues. Elle
// est retirée dès la confirmation, sinon elle recouvrirait à jamais les rows
// venues du serveur à chaque ré-hydratation d'après pull.
const pendingWrites = [];

let version = 0;
const listeners = new Set();

function emit() {
  version += 1;
  for (const fn of listeners) fn();
}

// Abonnement pour les hooks React (useSyncExternalStore).
export function subscribeUser(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getUserVersion() {
  return version;
}

// Vide la file dans l'ordre. Les puts sont émis SANS await entre eux, et c'est
// volontaire : `putUserData` appelle `db.put` avant son premier await, donc idb
// ouvre toutes les transactions de vidange pendant l'appel synchrone à
// `attachUserDb`, avant celles que DataProvider ouvre ensuite (migration, seed,
// relecture). IndexedDB exécute les transactions de même portée dans leur ordre
// de création : le `getAllUserData` qui suit voit donc les rows vidangées.
function flushPendingWrites(database) {
  for (const entry of pendingWrites.slice()) {
    putUserData(database, {
      scope: entry.scope,
      item_key: entry.item_key,
      data: entry.data,
      deleted: entry.deleted,
    })
      .catch((e) =>
        console.error('[userStore] persist differee echouee:', entry.scope, entry.item_key, e)
      )
      .then(() => {
        // Écrite (ou perdue) : on la retire. Une entrée qui resterait en file
        // recouvrirait la row serveur à chaque ré-hydratation d'après pull.
        const i = pendingWrites.indexOf(entry);
        if (i !== -1) pendingWrites.splice(i, 1);
      });
  }
}

// Attache le handle IndexedDB (write-through), puis vide la file des écritures
// faites avant l'attachement. Sans handle, le miroir reste en mémoire et les
// écritures sont mises en file, jamais jetées.
export function attachUserDb(database) {
  db = database;
  flushPendingWrites(database);
}

// Hydrate le miroir depuis les rows du store userData (exclut les deleted).
export function hydrateUserMirror(rows) {
  for (const scope of Object.keys(mirror)) mirror[scope] = {};
  for (const r of rows || []) {
    if (r.deleted) continue;
    if (!mirror[r.scope]) mirror[r.scope] = {};
    mirror[r.scope][r.item_key] = r.data;
  }
  // Rejoue par-dessus les écritures pas encore confirmées en base : hydrater
  // d'abord, appliquer ensuite. L'inverse reperdrait une valeur écrite avant
  // l'attachement, puisque les rows relues ne la contiennent pas encore.
  for (const entry of pendingWrites) {
    if (!mirror[entry.scope]) mirror[entry.scope] = {};
    if (entry.deleted) delete mirror[entry.scope][entry.item_key];
    else mirror[entry.scope][entry.item_key] = entry.data;
  }
  emit();
}

// ── Settings (scope 'setting') : valeur typee directe ──────────────────────
export function getSetting(key, fallback) {
  return key in mirror.setting ? mirror.setting[key] : fallback;
}

export function setSetting(key, value) {
  mirror.setting[key] = value;
  emit();
  if (db) {
    putUserData(db, { scope: 'setting', item_key: key, data: value }).catch((e) =>
      console.error('[userStore] persist setting echoue:', key, e)
    );
  }
}

// ── Collections (progression/preset/pattern/purchase) : objet complet ───────
export function getUserItems(scope) {
  return Object.values(mirror[scope] || {});
}

export function getUserMap(scope) {
  return { ...(mirror[scope] || {}) };
}

export function getUserItem(scope, itemKey) {
  return mirror[scope]?.[itemKey];
}

export function saveUserItem(scope, itemKey, obj) {
  if (!mirror[scope]) mirror[scope] = {};
  mirror[scope][itemKey] = obj;
  emit();
  if (db) {
    putUserData(db, { scope, item_key: itemKey, data: obj }).catch((e) =>
      console.error('[userStore] persist item echoue:', scope, itemKey, e)
    );
  } else {
    // Base pas encore attachée : on empile au lieu de jeter (cf. pendingWrites).
    pendingWrites.push({ scope, item_key: itemKey, data: obj, deleted: false });
  }
}

export function deleteUserItem(scope, itemKey) {
  const prev = mirror[scope]?.[itemKey];
  if (mirror[scope]) delete mirror[scope][itemKey];
  emit();
  if (db) {
    // Suppression logique : on garde la derniere data connue, deleted=true.
    putUserData(db, { scope, item_key: itemKey, data: prev ?? {}, deleted: true }).catch((e) =>
      console.error('[userStore] delete item echoue:', scope, itemKey, e)
    );
  } else {
    // Base pas encore attachée : la suppression logique attend elle aussi.
    pendingWrites.push({ scope, item_key: itemKey, data: prev ?? {}, deleted: true });
  }
}

// Ecriture d'une row dont le SERVEUR est proprietaire (aujourd'hui la seule est
// `purchase`/`patreon`), telle qu'il vient de la renvoyer. Jamais dirty : elle
// ne remonte pas au push, le serveur l'ignorerait de toute facon.
//
// Le updated_at est celui du SERVEUR, pas l'horloge du device : c'est ce qui
// permet a un tombstone envoye plus tard de gagner l'arbitrage LWW. Le miroir
// ne bouge que si IndexedDB a accepte l'ecriture, pour que les deux racontent
// la meme histoire.
export async function applyServerRow(row) {
  const { scope, item_key, data, deleted } = row;
  if (db && !(await applyPulled(db, row))) return false;
  if (!mirror[scope]) mirror[scope] = {};
  if (deleted) delete mirror[scope][item_key];
  else mirror[scope][item_key] = data;
  emit();
  return true;
}

// ── Device id ───────────────────────────────────────────────────────────────
// Identifiant local, propre a cette installation, persiste hors du scope 'setting'
// (qui, lui, se synchronise : un device_id ne doit PAS voyager entre appareils).
// Sert a lier une redemption de code beta a un appareil, sans compte user. Genere
// une fois, puis stable ; survit au "Reset User Data" (qui ne touche pas cette cle).
const DEVICE_ID_KEY = 'upscale_device_id';

export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Pour les tests : remet le miroir et le handle a zero.
export function _resetUserStore() {
  for (const scope of Object.keys(mirror)) mirror[scope] = {};
  db = null;
  pendingWrites.length = 0;
  version = 0;
  listeners.clear();
}
